import { useState, useEffect, useRef } from 'react';

import DdPopoverSelector, { DdPopoverSelectorProps } from '~src/shared/selectors/DdPopoverSelector';
import DdSearchContent, { OptionItem, SearchContentProps } from '~src/shared/selectors/DdSearchContent';

interface OwnProps<OPTTYPE = string[]|OptionItem> {
  initialOpt?: OPTTYPE;
  inactive?: boolean;
  maxWidth?: string;
  multiSelect?: boolean;
  onSelect: (option: OPTTYPE) => void;
  resetSelected?(): void;
  getInitiallySelectedOptionName?(id: string): Promise<string>;
}

type Props<OPTTYPE> = OwnProps<OPTTYPE> &
  Pick<DdPopoverSelectorProps, 'triggerIconName' | 'inactiveKey'> &
  Pick<
    SearchContentProps,
    | 'defaultOpt'
    | 'placeholder'
    | 'optionsList'
    | 'loadingSearch'
    | 'nothingFoundKey'
    | 'noMoreItems'
    | 'noSearch'
  > &
  Partial<Pick<SearchContentProps, 'fetchItems'>>;

export function SearchListSelector<OPTTYPE>(props: Props<OPTTYPE>) {
  const {onSelect, initialOpt, multiSelect, ...selectorProps } = props;
  const [isPopoverVisible, setIsPopoverVisible] = useState(false);
  const [selectedOpt, setSelectedOpt] = useState(getInitOpt);
  const loadingInitiallySelectedName = useRef(false);

  useEffect(() => {
    const options = getInitOpt();
    if (options) {
      if (Array.isArray(options) && options[0] && !options[0].name && !loadingInitiallySelectedName.current) {
        void getInitiallySelectedOptionName(options);
      } else {
        setSelectedOpt(options);
      }
    }
  }, [initialOpt, props.optionsList]);

  function getInitOpt() {
    if (Array.isArray(initialOpt)) {
      return props.optionsList!.length
        ? initialOpt.reduce<OptionItem[]>((acc, id) => {
            const opt = props.optionsList!.find((item) => item.id === id);

            acc.push(opt || { id });
            return acc;
          }, [])
        : undefined;
    } else {
      return initialOpt;
    }
  }

  async function getInitiallySelectedOptionName(opts: OptionItem[]) {
    if (props.getInitiallySelectedOptionName) {
      const [firstOpt] = opts;
      loadingInitiallySelectedName.current = true;
      const optName = await props.getInitiallySelectedOptionName(firstOpt.id);
      opts[0] = { ...firstOpt, name: optName };
      setSelectedOpt(opts);
      loadingInitiallySelectedName.current = false;
    }
  }

  const togglePopover = (visible: boolean) => {
    setIsPopoverVisible(visible);
  };

  const onOptClick = (id: string) => {
    if (props.optionsList) {
      const selectedItem = (props.optionsList.find((item) => item.id === id) || props.defaultOpt) as OptionItem;
      onSelect(selectedItem);
      setSelectedOpt(selectedItem);
    }
    togglePopover(false);
  };

  const applyChecked = (ids: string[]) => {
    const checkedOpts = ids.map((id) => {
      const checkedOpt = props.optionsList!.find((item) => item.id === id);
      if (!checkedOpt && selectedOpt) {
        return (selectedOpt as OptionItem[]).find((item) => item.id === id) as OptionItem;
      }
      return checkedOpt as OptionItem;
    });
    onSelect(ids);
    setSelectedOpt(checkedOpts);
    togglePopover(false);
  };

  const resetSelected = () => {
    if (props.resetSelected) props.resetSelected();
    setSelectedOpt([]);
  };

  const fetchItemsW = (params?: string, loadMore?: boolean) => {
    if (loadMore && props.noMoreItems) {
      return Promise.resolve();
    }
    return props.fetchItems ? props.fetchItems(params, loadMore) : Promise.resolve();
  };

  const popoverContent = (
    <DdSearchContent
      onOptClick={onOptClick}
      defaultOpt={props.defaultOpt}
      placeholder={props.placeholder}
      optionsList={props.optionsList}
      loadingSearch={props.loadingSearch}
      fetchItems={fetchItemsW}
      nothingFoundKey={props.nothingFoundKey}
      noMoreItems={props.noMoreItems}
      isPopoverVisible={isPopoverVisible}
      selectedOpt={selectedOpt}
      inactive={props.inactive}
      noSearch={props.noSearch}
      multiSelect={multiSelect}
      applyChecked={applyChecked}
    />
  );

  return (
    <DdPopoverSelector
      {...selectorProps}
      isPopoverVisible={isPopoverVisible}
      maxWidth={props.maxWidth}
      togglePopover={togglePopover}
      selectedOption={selectedOpt}
      popoverContent={popoverContent}
      withReset={!!props.resetSelected}
      resetSelected={resetSelected}
    />
  );
}

export default SearchListSelector;
